import React, { useState, useRef, useEffect, useMemo } from 'react';

/** Sentinel value representing "no fire department selected" in the option list. */
const NONE_OPTION_VALUE = '__none__';

/** Placeholder shown in the search input when no department is selected. */
const PLACEHOLDER_TEXT = 'Search fire departments...';

/** Id applied to the listbox element, referenced by the input's aria-controls. */
const LISTBOX_ID = 'fire-dept-selector-listbox';

/** Label for the option that clears the current selection. */
const NONE_OPTION_LABEL = 'None';

/**
 * Builds the secondary display line for a fire department row from its address,
 * joining city and state/province and omitting parts that are empty.
 * @param {Object} address - The FireDepartment's address
 * @returns {string} A comma-joined "city, state" string, or an empty string if both are blank
 */
function formatLocation(address) {
  if (!address) {
    return '';
  }
  return [address.cityAddr, address.stateProvinceAddr].filter(Boolean).join(', ');
}

/**
 * Filters fire departments by a case-insensitive substring match against name.
 * @param {Array<Object>} fireDepartments - The full list of fire departments
 * @param {string} searchText - The current search text
 * @returns {Array<Object>} The filtered list of fire departments
 */
function filterFireDepartments(fireDepartments, searchText) {
  const normalizedSearch = searchText.trim().toLowerCase();
  if (!normalizedSearch) {
    return fireDepartments;
  }
  return fireDepartments.filter((department) =>
    department.name.toLowerCase().includes(normalizedSearch)
  );
}

/**
 * FireDepartmentSelector - searchable, scrollable dropdown for selecting or clearing
 * a user's associated fire department.
 * @param {Object} props - Component props
 * @param {Array<Object>} props.fireDepartments - The fetched list of FireDepartment objects
 * @param {string|null} props.selectedFireDeptId - The currently selected fire department id, or null
 * @param {Function} props.onChange - Called with the new fire department id, or null when cleared
 * @param {boolean} props.disabled - Whether the selector is disabled
 * @param {string} [props.inputId] - The id applied to the search input, for label association
 * @returns {JSX.Element} The rendered fire department selector
 */
const FireDepartmentSelector = ({ fireDepartments, selectedFireDeptId, onChange, disabled, inputId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [focusIndex, setFocusIndex] = useState(-1);
  const containerRef = useRef(null);

  const selectedDepartment = useMemo(
    () => fireDepartments.find((department) => department.id === selectedFireDeptId) || null,
    [fireDepartments, selectedFireDeptId]
  );

  const filteredDepartments = useMemo(
    () => filterFireDepartments(fireDepartments, searchText),
    [fireDepartments, searchText]
  );

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    /**
     * Closes the dropdown when a click occurs outside the selector's container.
     * @param {MouseEvent} event - The document-level mousedown event
     * @returns {void}
     */
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  /**
   * Opens the dropdown and resets the search text to reflect the current input contents.
   * @returns {void}
   */
  const handleFocus = () => {
    setIsOpen(true);
    setFocusIndex(-1);
  };

  /**
   * Updates the search text as the user types and ensures the dropdown is open.
   * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event
   * @returns {void}
   */
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setIsOpen(true);
    setFocusIndex(-1);
  };

  /**
   * Selects a fire department (or clears the selection) and closes the dropdown.
   * @param {string} fireDeptId - The selected fire department id, or NONE_OPTION_VALUE to clear
   * @returns {void}
   */
  const selectOption = (fireDeptId) => {
    onChange(fireDeptId === NONE_OPTION_VALUE ? null : fireDeptId);
    setSearchText('');
    setIsOpen(false);
    setFocusIndex(-1);
  };

  /**
   * Handles keyboard navigation within the dropdown: arrow keys move focus,
   * Enter selects the focused option, Escape closes the dropdown.
   * @param {React.KeyboardEvent} e - The keydown event
   * @returns {void}
   */
  const handleKeyDown = (e) => {
    if (!isOpen) {
      return;
    }

    const optionCount = filteredDepartments.length + 1;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIndex((prev) => (prev + 1) % optionCount);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIndex((prev) => (prev - 1 + optionCount) % optionCount);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (focusIndex === 0) {
        selectOption(NONE_OPTION_VALUE);
      } else if (focusIndex > 0) {
        selectOption(filteredDepartments[focusIndex - 1].id);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setFocusIndex(-1);
    }
  };

  const inputValue = isOpen ? searchText : selectedDepartment?.name || '';

  return (
    <div className="fire-dept-selector" ref={containerRef}>
      <input
        type="text"
        id={inputId}
        className="fire-dept-selector-input"
        value={inputValue}
        onChange={handleSearchChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={PLACEHOLDER_TEXT}
        disabled={disabled}
        role="combobox"
        aria-expanded={isOpen}
        aria-autocomplete="list"
        aria-controls={LISTBOX_ID}
      />
      {isOpen && (
        <ul id={LISTBOX_ID} className="fire-dept-selector-list" role="listbox">
          <li
            className={`fire-dept-selector-option fire-dept-selector-none-option${
              focusIndex === 0 ? ' fire-dept-selector-option-focused' : ''
            }`}
            role="option"
            aria-selected={selectedFireDeptId === null}
            onMouseDown={() => selectOption(NONE_OPTION_VALUE)}
          >
            {NONE_OPTION_LABEL}
          </li>
          {filteredDepartments.map((department, index) => {
            const location = formatLocation(department.address);
            return (
              <li
                key={department.id}
                className={`fire-dept-selector-option${
                  focusIndex === index + 1 ? ' fire-dept-selector-option-focused' : ''
                }`}
                role="option"
                aria-selected={department.id === selectedFireDeptId}
                onMouseDown={() => selectOption(department.id)}
              >
                <span className="fire-dept-selector-option-name">{department.name}</span>
                {location && (
                  <span className="fire-dept-selector-option-location">
                    {location}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default FireDepartmentSelector;
